import React, { FC } from "react";
import styles from "./styles.module.scss";
import LocationItem from "./LocationItem";
import { KatIcon, KatSpinner } from "@amzn/katal-react";
import {
  useTranslation,
  WithTranslation,
  withTranslation,
} from "react-i18next";
import { updateUrlParams } from "src/utils";
import { useHistory } from "react-router-dom";
import { Country, CountrySelection } from "src/api/interfaces/country";
import {
  DomainName,
  FilterValue,
} from "src/components/Contexts/FilterOptions/types";
import i18n from "src/i18n";

export interface CountryListProps {
  availableCountries: Country[];
  selectedCountry?: Country;
  setCountrySelection: (updates: any) => void;
  isFetching: boolean;
  disableCountryChange: boolean | undefined;
}

export const countryFlagIconName = (country: string) =>
  `flag-${country.toLowerCase()}` as KatIcon.Name;

export const COUNTRY_QS_KEY = "country";

const FALLBACK_LANGUAGE = "en-US";
const CountryList: FC<CountryListProps & WithTranslation> = ({
  availableCountries,
  selectedCountry,
  setCountrySelection,
  isFetching,
  disableCountryChange,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  const options: KatalDropdownOption[] = availableCountries
    .map(({ countryCode }) => ({
      name: countryCode,
      value: countryCode,
      iconName: countryFlagIconName(countryCode),
    }))
    .sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const handleCountryChange = (value: string) => {
    if (!value) return;

    const urlParamsToUpdate = [
      [COUNTRY_QS_KEY, value] as [string, FilterValue],
      ...(Object.values(DomainName).map((domainName: string) => [
        domainName,
        undefined,
      ]) as [string, undefined][]),
    ];

    updateUrlParams(urlParamsToUpdate, history);

    setCountrySelection((prevCountrySelection: CountrySelection) => ({
      ...prevCountrySelection,
      selectedCountry: {
        countryCode: value,
      },
    }));
  };

  const currentLanguage = i18n.language ?? FALLBACK_LANGUAGE;
  const countryNameResolver = new Intl.DisplayNames(
    [currentLanguage, FALLBACK_LANGUAGE],
    {
      type: "region",
    }
  );

  return (
    <div className={styles.list_container}>
      <h1>{t("aic_country_selection_label")}</h1>
      {!isFetching ? (
        <div
          className={styles.list}
          style={{
            pointerEvents: disableCountryChange ? "none" : "auto",
            opacity: disableCountryChange ? 0.5 : 1,
          }}
        >
          {options.map(({ value, name, iconName }) => (
            <LocationItem
              key={name}
              value={value}
              name={countryNameResolver.of(name) ?? name}
              iconName={iconName!}
              isSelected={selectedCountry?.countryCode === value}
              onClick={disableCountryChange ? () => {} : handleCountryChange}
            />
          ))}
        </div>
      ) : (
        <KatSpinner />
      )}
    </div>
  );
};

export default withTranslation()(CountryList);
